import { Injectable, InjectionToken, Injector, ElementRef } from "@angular/core";
import { Overlay, OverlayRef, OverlayConfig, ScrollStrategy, PositionStrategy } from "@angular/cdk/overlay";
import { ComponentPortal } from '@angular/cdk/portal';
import { Type } from "@angular/core";

// Enhanced configuration interface
export interface DynamicOverlayConfig {
  // Backdrop options
  hasBackdrop?: boolean;
  backdropClass?: string | string[];
  disableClose?: boolean; // Prevent closing on backdrop click
  
  // Positioning options
  position?: 'center' | 'top' | 'bottom' | 'custom';
  positionStrategy?: PositionStrategy;
  
  // For element-relative positioning
  relativeTo?: ElementRef | HTMLElement;
  offsetX?: number;
  offsetY?: number;
  
  // Scroll strategy
  scrollStrategy?: ScrollStrategy | 'reposition' | 'block' | 'close' | 'noop';
  
  // Panel options
  panelClass?: string | string[];
  width?: string | number;
  height?: string | number;
  minWidth?: string | number;
  minHeight?: string | number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  
  // Animation
  disposeOnNavigation?: boolean;
}

@Injectable({ providedIn: 'root' })
export class OverlayService {
  private overlayRefs: Map<string, OverlayRef> = new Map();
  private currentOverlayId = 0;
  scrollStrategies: any;

  constructor(private overlay: Overlay, private injector: Injector) {}

  open<T>(
    component: Type<T>, 
    config?: DynamicOverlayConfig, 
    data?: any
  ): string {
    const overlayId = `overlay_${++this.currentOverlayId}`;
    const overlayConfig = this.createOverlayConfig(config);
    
    const overlayRef = this.overlay.create(overlayConfig);
    this.overlayRefs.set(overlayId, overlayRef);

    // Handle backdrop clicks
    if (config?.hasBackdrop !== false && !config?.disableClose) {
      overlayRef.backdropClick().subscribe(() => {
        this.close(overlayId);
      });
    }

    // Handle window resize and scroll events for position-dependent overlays
    if (config?.relativeTo || config?.position !== 'center') {
      this.setupAutoClose(overlayRef, overlayId, config);
    }

    // Create dependency injection providers
    const providers: any[] = [
      { provide: OverlayRef, useValue: overlayRef },
      { provide: OVERLAY_ID, useValue: overlayId }
    ];

    if (data !== undefined) {
      providers.push({ provide: OVERLAY_DATA, useValue: data });
    }

    // Create and attach component portal
    const portal = new ComponentPortal(
      component,
      null,
      Injector.create({
        providers,
        parent: this.injector
      })
    );

    overlayRef.attach(portal);
    return overlayId;
  }

  close(overlayId?: string) {
    if (overlayId) {
      const overlayRef = this.overlayRefs.get(overlayId);
      if (overlayRef) {
        overlayRef.dispose();
        this.overlayRefs.delete(overlayId);
      }
    } else {
      // Close all overlays
      this.overlayRefs.forEach((overlayRef, id) => {
        overlayRef.dispose();
      });
      this.overlayRefs.clear();
    }
  }

  closeAll() {
    this.close();
  }

  private createOverlayConfig(config?: DynamicOverlayConfig): OverlayConfig {
    const overlayConfig = new OverlayConfig();

    if (config) {
      // Backdrop configuration
      overlayConfig.hasBackdrop = config.hasBackdrop ?? true;
      if (config.backdropClass) {
        overlayConfig.backdropClass = config.backdropClass;
      } else if (overlayConfig.hasBackdrop) {
        overlayConfig.backdropClass = 'cdk-overlay-dark-backdrop';
      }

      // Position strategy
      overlayConfig.positionStrategy = this.createPositionStrategy(config);
      
      // Scroll strategy
      overlayConfig.scrollStrategy = this.createScrollStrategy(config);
      
      // Panel styling
      if (config.panelClass) overlayConfig.panelClass = config.panelClass;
      if (config.width) overlayConfig.width = config.width;
      if (config.height) overlayConfig.height = config.height;
      if (config.minWidth) overlayConfig.minWidth = config.minWidth;
      if (config.minHeight) overlayConfig.minHeight = config.minHeight;
      if (config.maxWidth) overlayConfig.maxWidth = config.maxWidth;
      if (config.maxHeight) overlayConfig.maxHeight = config.maxHeight;
      
      // Other options
      if (config.disposeOnNavigation !== undefined) {
        overlayConfig.disposeOnNavigation = config.disposeOnNavigation;
      }
    } else {
      // Default configuration
      overlayConfig.hasBackdrop = true;
      overlayConfig.backdropClass = 'cdk-overlay-dark-backdrop';
      overlayConfig.positionStrategy = this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically();
      overlayConfig.scrollStrategy = this.overlay.scrollStrategies.block();
    }

    return overlayConfig;
  }

  private createPositionStrategy(config: DynamicOverlayConfig): PositionStrategy {
    // If custom position strategy is provided, use it
    if (config.positionStrategy) {
      return config.positionStrategy;
    }

    // If relative positioning is specified
    if (config.relativeTo) {
      const element = config.relativeTo instanceof ElementRef 
        ? config.relativeTo.nativeElement 
        : config.relativeTo;
        
      return this.overlay
        .position()
        .flexibleConnectedTo(element)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetX: config.offsetX || 0,
            offsetY: config.offsetY || 0
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetX: config.offsetX || 0,
            offsetY: config.offsetY || 0
          }
        ]);
    }

    // Global positioning based on position option
    const globalPosition = this.overlay.position().global();
    
    switch (config.position) {
      case 'top':
        return globalPosition.centerHorizontally().top('20px');
      case 'bottom':
        return globalPosition.centerHorizontally().bottom('20px');
      case 'center':
      default:
        return globalPosition.centerHorizontally().centerVertically();
    }
  }

  private createScrollStrategy(config: DynamicOverlayConfig): ScrollStrategy {
    if (config.scrollStrategy) {
      if (typeof config.scrollStrategy === 'string') {
        switch (config.scrollStrategy) {
          case 'reposition':
            return this.overlay.scrollStrategies.reposition();
          case 'block':
            return this.overlay.scrollStrategies.block();
          case 'close':
            return this.overlay.scrollStrategies.close();
          case 'noop':
            return this.overlay.scrollStrategies.noop();
          default:
            return this.overlay.scrollStrategies.block();
        }
      } else {
        return config.scrollStrategy;
      }
    }
    
    return this.overlay.scrollStrategies.block();
  }

  private setupAutoClose(overlayRef: OverlayRef, overlayId: string, config?: DynamicOverlayConfig) {
    const resizeHandler = () => {
      // Close overlay on window resize
      this.close(overlayId);
    };

    const scrollHandler = () => {
      // Check if trigger element is still visible
      if (config?.relativeTo) {
        const element = config.relativeTo instanceof ElementRef 
          ? config.relativeTo.nativeElement 
          : config.relativeTo;
        
        if (!this.isElementVisible(element)) {
          this.close(overlayId);
        }
      }
    };

    // Add event listeners
    window.addEventListener('resize', resizeHandler);
    
    // Clean up when overlay is disposed
    overlayRef.detachments().subscribe(() => {
      window.removeEventListener('resize', resizeHandler);

    });
  }

  private isElementVisible(element: HTMLElement): boolean {
    if (!element) return false;
    
    const rect = element.getBoundingClientRect();
    const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
    const viewWidth = Math.max(document.documentElement.clientWidth, window.innerWidth);
    
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < viewHeight &&
      rect.left < viewWidth
    );
  }
}

// Injection tokens
export const OVERLAY_DATA = new InjectionToken<any>('OVERLAY_DATA');
export const OVERLAY_ID = new InjectionToken<string>('OVERLAY_ID');